import { useCallback, useEffect, useRef } from "react"
import {
  getAbsoluteApiUrl,
  getPlayerEventsBatchPath,
  logPlayerEventsBatch,
} from "../api/educationalVideos"
import { createId } from "../utils/visitor"

const FLUSH_INTERVAL_MS = 30 * 1000
// Flush early at this size. Also the API's per-request maximum, and ~30 KB of
// JSON, comfortably inside sendBeacon's 64 KB limit.
const MAX_BATCH = 100
// A dead API must not grow memory without limit; the oldest go first.
const MAX_QUEUED = 500
const MAX_ATTEMPTS = 3

// No response (offline, blocked), the server failing, or rate limiting may
// clear up by the next flush. Any other 4xx means the request itself is
// wrong, and retrying it would only block every event queued behind it.
const isTemporaryFailure = (error) => {
  const status = error?.response?.status
  return !status || status >= 500 || status === 429
}

// Queue entries are { event, capturedAt, attempts }. ageMs is measured with
// performance.now(), so the API can backdate each event without trusting the
// device's clock.
const toPayload = ({ event, capturedAt }, now) => ({
  ...event,
  ageMs: Math.max(0, Math.round(now - capturedAt)),
})

const sendWithBeacon = (url, events) => {
  const body = JSON.stringify({ events })

  if (navigator.sendBeacon) {
    return navigator.sendBeacon(
      url,
      new Blob([body], { type: "application/json" })
    )
  }

  // Older browsers. keepalive lets the request outlive the page; where that
  // flag isn't supported either, it's an ordinary request that may be cut off
  // by the unload — the best such a browser allows.
  if (typeof fetch !== "function") return false
  fetch(url, {
    method: "POST",
    keepalive: true,
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body,
  }).catch(() => {})
  return true
}

/*
  One queue for every player on the patient page. Players hand events to the
  returned `enqueue`; nothing is sent per event.

  - Every 30 s (or at 100 events) the queue is sent through the API. An empty
    queue sends nothing.
  - When the tab is hidden or the page is closing, whatever is left — including
    a batch still in flight, which the unload may cancel — goes out with
    sendBeacon. The API skips events it already stored (by clientEventId), so
    sending a batch twice is safe; losing it is not.

  `enabled: false` (admin preview opens, or no report yet) queues and sends
  nothing.

  The report page passes its `uuid`; the event QR page passes its own
  `batchPath` instead.
*/
export const usePlayerEventQueue = ({ uuid, batchPath, enabled }) => {
  const path = batchPath || (uuid ? getPlayerEventsBatchPath(uuid) : null)
  const stateRef = useRef({
    pending: [],
    inFlight: [],
    flushing: false,
    sequence: 0,
  })
  const enabledRef = useRef(enabled)
  enabledRef.current = enabled && !!path
  const flushRef = useRef(() => {})

  useEffect(() => {
    if (!enabled || !path) return

    const state = stateRef.current
    const beaconUrl = getAbsoluteApiUrl(path)

    const flush = async () => {
      if (state.flushing || !state.pending.length) return

      state.flushing = true
      const batch = state.pending.splice(0, MAX_BATCH)
      state.inFlight = batch
      let succeeded = false

      try {
        const now = performance.now()
        await logPlayerEventsBatch({
          path,
          events: batch.map((entry) => toPayload(entry, now)),
        })
        succeeded = true
      } catch (error) {
        // A beacon took over this batch while the request was out, so it is
        // no longer ours to retry.
        if (state.inFlight === batch && isTemporaryFailure(error)) {
          const retry = batch.filter(
            (entry) => (entry.attempts += 1) < MAX_ATTEMPTS
          )
          state.pending.unshift(...retry)
          if (state.pending.length > MAX_QUEUED) {
            state.pending.splice(0, state.pending.length - MAX_QUEUED)
          }
        }
      } finally {
        if (state.inFlight === batch) state.inFlight = []
        state.flushing = false
      }

      if (succeeded && state.pending.length >= MAX_BATCH) flush()
    }

    const flushWithBeacon = () => {
      const entries = [...state.inFlight, ...state.pending]
      if (!entries.length) return

      const now = performance.now()
      const unsent = []
      for (let start = 0; start < entries.length; start += MAX_BATCH) {
        const chunk = entries.slice(start, start + MAX_BATCH)
        const queued = sendWithBeacon(
          beaconUrl,
          chunk.map((entry) => toPayload(entry, now))
        )
        // sendBeacon refuses when the browser's quota is used up; keep those
        // for the next hide or flush.
        if (!queued) unsent.push(...chunk)
      }

      state.inFlight = []
      state.pending = unsent
    }

    // visibilitychange covers tab and app switches, and closing on iOS (which
    // may never fire pagehide); pagehide covers close, reload and navigation.
    // No beforeunload: it would keep the page out of the back/forward cache.
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") flushWithBeacon()
    }

    flushRef.current = flush
    const intervalId = setInterval(flush, FLUSH_INTERVAL_MS)
    document.addEventListener("visibilitychange", onVisibilityChange)
    window.addEventListener("pagehide", flushWithBeacon)

    return () => {
      clearInterval(intervalId)
      document.removeEventListener("visibilitychange", onVisibilityChange)
      window.removeEventListener("pagehide", flushWithBeacon)
      flushRef.current = () => {}
      flushWithBeacon()
    }
  }, [path, enabled])

  return useCallback((event) => {
    if (!enabledRef.current) return

    const state = stateRef.current
    state.pending.push({
      event: {
        ...event,
        clientEventId: createId(),
        // One counter for the whole page, so order holds across videos.
        sequence: state.sequence++,
      },
      capturedAt: performance.now(),
      attempts: 0,
    })

    if (state.pending.length > MAX_QUEUED) {
      state.pending.splice(0, state.pending.length - MAX_QUEUED)
    }
    if (state.pending.length >= MAX_BATCH) flushRef.current()
  }, [])
}
